import {
  BackgroundCheck,
  BackgroundCheckStatus
} from "@wingspanhq/wingspan-api-sdk";

import { getRequirementDefinitionTableCellProps } from "../../../../../RequirementDefinitions/utils";
import {
  CellsConfig,
  ContextItem,
  RequirementAction,
  RequirementTableCallback,
  RequirementTableItemData
} from "../../../../types";
import { useBackgroundChecksStartModal } from "../../modals/useBackgroundChecksStartModal";
import { getBackgroundCheckPayeeRequirementStatus } from "../helpers/getBackgroundCheckPayeeRequirementStatus";
import { getBackgroundCheckProgressSteps } from "../helpers/getBackgroundCheckProgressSteps";
import { getBackgroundChecksRequirementName } from "../helpers/getBackgroundChecksRequirementName";

import { payerBackgroundCheckRenderCardDescription } from "./payerBackgroundCheckRenderCardDescription";

export const usePayerBackgroundChecksConfig = (
  callback: RequirementTableCallback
): CellsConfig<
  RequirementTableItemData,
  ContextItem,
  BackgroundCheck | undefined
> => {
  const backgroundChecksStartModal = useBackgroundChecksStartModal();

  const getCheckAction = (
    requirement: RequirementTableItemData,
    context: ContextItem,
    backgroundCheck: BackgroundCheck | undefined
  ) => {
    if (
      backgroundCheck?.applicationUrl &&
      backgroundCheck.status === BackgroundCheckStatus.PENDING
    ) {
      return async () => {
        await backgroundChecksStartModal.open({
          backgroundCheckId: backgroundCheck.id!
        });
      };
    }

    return undefined;
  };

  return {
    isActionRequired: (requirement, context, backgroundCheck) => {
      return !!(
        backgroundCheck?.applicationUrl &&
        backgroundCheck.status === BackgroundCheckStatus.PENDING
      );
    },
    requirementDetailsProgressWidget: (
      requirement,
      context,
      backgroundCheck
    ) => {
      return backgroundCheck?.status
        ? getBackgroundCheckProgressSteps(backgroundCheck.status)
        : undefined;
    },
    requirementStatusAlert: (requirement, context, backgroundCheck) => {
      if (backgroundCheck?.status === BackgroundCheckStatus.PENDING) {
        return {
          theme: "warning",
          title: "Action required",
          children:
            "Complete the requirement in order to be eligible to receive payments from your client.",
          icon: "alert"
        };
      }
    },
    requirementName: getBackgroundChecksRequirementName,
    requirementDetails: (requirement, context, backgroundCheck) => {
      const backgroundCheckAction = getCheckAction(
        requirement,
        context,
        backgroundCheck
      );

      return {
        header: {
          label: {
            text: getBackgroundChecksRequirementName(
              requirement,
              context,
              backgroundCheck
            )
          }
        },
        children: payerBackgroundCheckRenderCardDescription(
          requirement,
          backgroundCheck
        ),
        data: [],
        actions: backgroundCheckAction
          ? {
              alignment: "fill",
              buttons: [
                {
                  label: "Start background check",
                  onAsyncClick: backgroundCheckAction,
                  kind: "Primary",
                  visible: !!backgroundCheckAction
                }
              ]
            }
          : undefined
      };
    },
    toolbarActions: (requirement, context, backgroundCheck) => {
      const actions: RequirementAction[] = [];

      const backgroundCheckAction = getCheckAction(
        requirement,
        context,
        backgroundCheck
      );

      if (backgroundCheckAction) {
        actions.push({
          label: "Start background check",
          onAsyncClick: backgroundCheckAction,
          buttonKind: "Primary",
          icon: "open-tab",
          buttonIcon: null,
          mainAction: true
        });
      }

      return actions;
    },
    nameCell: (requirement, context, backgroundCheck) => {
      return {
        text: getBackgroundChecksRequirementName(
          requirement,
          context,
          backgroundCheck
        )
      };
    },
    typeCell: requirement =>
      getRequirementDefinitionTableCellProps({
        type: requirement.type
      }),
    statusCell: (requirement, context, backgroundCheck) => {
      return getBackgroundCheckPayeeRequirementStatus(
        requirement,
        context,
        backgroundCheck
      );
    }
  };
};
